import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import useSeo from "@/lib/seo";
import { GUIDES, LANDING_PAGES } from "@/lib/content";
import { ArrowRight, BookOpen, Sparkles } from "lucide-react";

export default function Blog() {
  useSeo({
    title: "Guides & Inspiration",
    description: "Honest, in-depth guides to UK caravan holidays — eco-friendly parks, hidden gems, dog-friendly picks and last-minute steals.",
    keywords: "uk caravan holiday guide, best caravan parks uk, eco caravan park, dog friendly caravan",
  });

  return (
    <div className="App">
      <Nav />
      <div className="bg-forest text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="label-eyebrow text-white/70">Guides &amp; Inspiration</span>
          <h1 className="font-display font-extrabold text-5xl mt-2" data-testid="blog-title">ParkScanner Guides</h1>
          <p className="text-white/80 mt-3 max-w-2xl text-lg leading-relaxed">
            Honest, opinionated picks across 180+ UK caravan parks. No paid placements in these guides — these are our editorial favourites by category.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-display font-bold text-2xl mb-5 flex items-center gap-2"><BookOpen className="w-6 h-6 text-forest" /> Latest articles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-articles">
          {GUIDES.map((g) => (
            <Link
              key={g.slug}
              to={`/guides/${g.slug}`}
              className="bg-white border border-border rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:border-forest/30 transition-all group"
              data-testid={`blog-card-${g.slug}`}
            >
              <div className="aspect-[16/9] bg-gradient-to-br from-forest to-forest-light flex items-center justify-center text-7xl">
                {g.heroEmoji}
              </div>
              <div className="p-5">
                <div className="label-eyebrow">{g.category}</div>
                <h3 className="font-display font-semibold text-xl mt-2 leading-snug group-hover:text-forest transition-colors">{g.title}</h3>
                <p className="text-sm text-ink/70 mt-2 line-clamp-3">{g.excerpt}</p>
                <div className="flex items-center gap-2 text-xs text-ink/50 mt-3">
                  <span>{new Date(g.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  <span>·</span>
                  <span>{g.readMinutes} min read</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <h2 className="font-display font-bold text-2xl mt-16 mb-5 flex items-center gap-2"><Sparkles className="w-6 h-6 text-terracotta" /> Hand-curated park collections</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {LANDING_PAGES.map((l) => (
            <Link
              key={l.slug}
              to={`/collections/${l.slug}`}
              className="bg-white border border-border rounded-2xl p-5 hover:border-forest hover:shadow-md transition-all"
              data-testid={`landing-link-${l.slug}`}
            >
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: l.accent }}>{l.badge}</span>
              <h3 className="font-display font-bold text-lg mt-2">{l.h1}</h3>
              <span className="inline-flex items-center gap-1 text-sm text-forest mt-3">Open collection <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
